import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Primary gradient button component used throughout the app
 * Features purple gradient, rounded pill shape, hover animations
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {boolean} props.loading - Whether button is in loading state
 * @param {string} props.type - Button type (button, submit, etc.)
 * @param {Object} props.style - Additional inline styles to merge
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {string} props.variant - Style variant: 'default', 'compact', 'icon'
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.rest - Any other props to pass through
 */
const PrimaryButton = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  style = {},
  size = 'md',
  variant = 'default',
  className = '',
  ...rest
}) => {
  const { colors } = useTheme();
  const isDisabled = disabled || loading;

  const primaryRgb = '99, 102, 241';
  const gradient = `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})`;
  const shadowBase = `0 2px 8px rgba(${primaryRgb}, 0.2)`;
  const shadowHover = `0 4px 12px rgba(${primaryRgb}, 0.3)`;

  // Size configurations
  const sizeStyles = {
    sm: {
      fontSize: '0.85em',
      padding: '6px 16px',
    },
    md: {
      fontSize: '0.95em',
      padding: '8px 20px',
    },
    lg: {
      fontSize: '1.1em',
      padding: '12px 28px',
    },
  };

  // Variant configurations
  const variantStyles = {
    default: {
      borderRadius: '50px',
      minWidth: '120px',
    },
    compact: {
      borderRadius: '50px',
      minWidth: 'auto',
    },
    icon: {
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      minWidth: '28px',
      minHeight: '28px',
      padding: 0,
      aspectRatio: '1 / 1',
    },
  };

  const baseStyle = {
    background: gradient,
    color: '#fff',
    border: 'none',
    fontWeight: '600',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: shadowBase,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.7 : 1,
    whiteSpace: 'nowrap',
    lineHeight: variant === 'icon' ? 1 : 'normal',
    ...sizeStyles[size],
    ...variantStyles[variant],
    ...style,
  };

  const handleMouseEnter = (e) => {
    if (!isDisabled) {
      if (variant === 'icon') {
        e.currentTarget.style.transform = 'scale(1.05)';
      } else {
        e.currentTarget.style.transform = 'translateY(-2px)';
      }
      e.currentTarget.style.boxShadow = shadowHover;
    }
  };

  const handleMouseLeave = (e) => {
    if (!isDisabled) {
      if (variant === 'icon') {
        e.currentTarget.style.transform = 'scale(1)';
      } else {
        e.currentTarget.style.transform = 'translateY(0)';
      }
      e.currentTarget.style.boxShadow = shadowBase;
    }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={className}
      style={baseStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      {children}
    </button>
  );
};

export default PrimaryButton;



